// Detail záznamu o úrazu (15. 10. 2026, schválený mockup): bloky záznamu, podpisy,
// pojišťovna, přílohy a historie změn. Koncept se dokončuje v průvodci (UrazNovy).
import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Bandage, MessageSquare, Clock, FileText, Pencil, Trash2, Camera, ShieldPlus } from "lucide-react";
import GradientHeader from "../../components/GradientHeader.jsx";
import { Stranka, Modal, HlavickaOkna } from "../../components/ui.jsx";
import { Pil, KartaLista } from "../../components/sales/salesUi.jsx";
import { toast } from "../../components/Toast.jsx";
import { confirmDialog } from "../../components/confirmHost.jsx";
import { apiFetch, useApiData } from "../../lib/api.js";
import { STAV, zaklad, fmtDatum, fmtKdy, POPISEK } from "./urazUi.jsx";

async function post(body) {
  const r = await apiFetch("/api/uraz", { method: "POST", body: JSON.stringify(body) });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `Chyba ${r.status}`);
  return j;
}

function Udaj({ label, children, cely }) {
  return (
    <div className={`min-w-0 ${cely ? "sm:col-span-2" : ""}`}>
      <div className="mb-1 text-[11px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
      <div className="whitespace-pre-line rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-800 [overflow-wrap:anywhere]">{children || <span className="font-normal text-slate-400">nevyplněno</span>}</div>
    </div>
  );
}
function Sekce({ n, nazev, children }) {
  return (
    <div className="mt-5 first:mt-0">
      <div className="mb-2 flex items-center gap-2"><span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-100 text-xs font-bold text-brand-deep">{n}</span><span className="text-sm font-bold uppercase tracking-wider text-brand-deep">{nazev}</span></div>
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </div>
  );
}
function Podpis({ kdo, stav, ton, detail, children }) {
  return (
    <div className="border-t border-slate-100 py-3 first:border-t-0 first:pt-0">
      <div className="font-bold text-slate-900 demo-sensitive">{kdo}</div>
      {detail && <div className="mt-0.5 text-[13px] text-slate-500">{detail}</div>}
      <div className="mt-2 flex flex-wrap items-center gap-2"><Pil ton={ton} casti={stav} />{children}</div>
    </div>
  );
}

export default function UrazDetail() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const base = zaklad(pathname);
  const navigate = useNavigate();
  const { data, loading, refetch } = useApiData(`/api/uraz?id=${id}`, { bezKese: true });
  const [busy, setBusy] = useState(false);
  const [projednat, setProjednat] = useState(false);
  const [poznamka, setPoznamka] = useState("");
  const [poj, setPoj] = useState(null);

  if (loading && !data) return <Stranka><div className="h-24 animate-pulse rounded-2xl bg-brand-light" /></Stranka>;
  const z = data?.zaznam;
  if (!z) return <Stranka><GradientHeader title="Kniha úrazů" icon={Bandage} /><div className="glass p-4 text-sm text-slate-600">{data?.error || "Záznam nenalezen."}</div></Stranka>;
  const prava = data.prava || {};
  const s = STAV[z.stav] || STAV.koncept;
  const pz = z.podpis_zraneny;

  const akce = async (fn, ok) => { setBusy(true); try { await fn(); if (ok) toast(ok, "success"); refetch(); } catch (e) { toast(e.message, "error"); } finally { setBusy(false); } };
  async function stahnout(p) {
    try {
      const r = await apiFetch(`/api/uraz?akce=priloha&id=${z.id}&key=${encodeURIComponent(p.key)}`);
      if (!r.ok) throw new Error("Přílohu se nepodařilo stáhnout.");
      const url = URL.createObjectURL(await r.blob());
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast(e.message, "error"); }
  }
  async function smazat() {
    const ok = await confirmDialog({ title: "Smazat koncept?", description: "Rozepsaný záznam se smaže i s historií.", confirmLabel: "Smazat", cancelLabel: "Zrušit", variant: "danger" });
    if (!ok) return;
    await akce(() => post({ akce: "smazat", id: z.id }));
    navigate(base, { replace: true });
  }

  const trenink = z.typ_treninku === "bootcamp" ? `Bootcamp${z.bootcamp_nazev ? `, ${z.bootcamp_nazev}` : ""}${z.trener_jmeno ? `, vedl(a) ${z.trener_jmeno}` : ""}`
    : z.typ_treninku === "osobni" ? `Osobní trénink${z.trener_jmeno ? `, vedl(a) ${z.trener_jmeno}` : ""}` : z.typ_treninku_text;

  return (
    <Stranka>
      <GradientHeader title={`Úraz č. ${z.id}`} icon={Bandage}
        akce={[
          ...(prava.upravit ? [{ klic: "upravit", ikona: Pencil, popis: z.stav === "koncept" ? "Dokončit záznam" : "Upravit záznam", onClick: () => navigate(`${base}/novy?id=${z.id}`) }] : []),
          ...(prava.smazat ? [{ klic: "smazat", ikona: Trash2, popis: "Smazat koncept", onClick: smazat }] : []),
        ]} />

      {z.stav === "koncept" && prava.upravit && (
        <button type="button" className="btn btn-primary w-full sm:w-auto" onClick={() => navigate(`${base}/novy?id=${z.id}`)}><Pencil className="h-5 w-5" /> Dokončit a podepsat</button>
      )}

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="min-w-0 lg:col-span-2">
          <KartaLista ikona={Bandage} nazev={<><span className="whitespace-nowrap">{fmtDatum(z.datum) || "Bez data"}{z.zraneny_jmeno ? "," : ""}</span>{z.zraneny_jmeno ? " " : ""}{z.zraneny_jmeno && <span className="whitespace-nowrap">{z.zraneny_jmeno}</span>}</>}>
            <div className="mb-4"><Pil ton={s.ton} casti={s.text} /></div>
            <Sekce n={1} nazev="Kdo a kdy">
              <Udaj label="Zraněný">{z.zraneny_jmeno ? `${z.zraneny_jmeno}${z.zraneny_typ_text ? `, ${z.zraneny_typ_text}` : ""}` : ""}</Udaj>
              <Udaj label="Telefon">{z.zraneny_telefon}</Udaj>
              <Udaj label="Datum a čas úrazu">{z.datum ? `${fmtDatum(z.datum)}${z.cas ? `, ${z.cas}` : ""}` : ""}</Udaj>
              <Udaj label="Místo">{z.misto}</Udaj>
              <Udaj label="Trénink">{trenink}</Udaj>
              <Udaj label="Zapsal(a)">{z.zapsal_jmeno ? `${z.zapsal_jmeno}, ${fmtKdy(z.created_at)}` : ""}</Udaj>
              {z.u_toho && <Udaj label="Kdo byl u toho" cely>{z.u_toho}</Udaj>}
            </Sekce>
            <Sekce n={2} nazev="Co se stalo">
              <Udaj label="Činnost, cvičení, náčiní a zátěž" cely>{z.cinnost}</Udaj>
              <Udaj label="Příčina a okolnosti" cely>{z.pricina}</Udaj>
            </Sekce>
            <Sekce n={3} nazev="Zranění a první pomoc">
              <Udaj label="Druh a rozsah zranění">{[z.zraneni_druh, z.zraneni_popis].filter(Boolean).join(", ")}</Udaj>
              <Udaj label="Postižená část těla">{[z.cast_tela, z.strana && z.strana !== "Neurčeno" ? z.strana.toLowerCase() : null].filter(Boolean).join(", ")}</Udaj>
              <Udaj label="První pomoc, kdo ji poskytl">{z.prvni_pomoc}</Udaj>
              <Udaj label="Záchranka a lékař">{[z.zachranka && `Záchranka: ${z.zachranka.toLowerCase()}`, z.lekar && `Lékař: ${z.lekar.toLowerCase()}`].filter(Boolean).join(", ")}</Udaj>
            </Sekce>
            <Sekce n={4} nazev="Svědci a opatření">
              {(z.svedci || []).length ? z.svedci.map((sv, i) => (
                <Udaj key={i} label={`Svědek ${i + 1}`}>{[sv.jmeno, sv.telefon, sv.email].filter(Boolean).join(", ")}</Udaj>
              )) : <Udaj label="Svědci">bez svědků</Udaj>}
              <Udaj label="Opatření proti opakování" cely>{z.opatreni}</Udaj>
            </Sekce>
            <Sekce n={5} nazev="Fotky a dokumenty">
              <div className="flex flex-wrap gap-2 sm:col-span-2">
                {(z.prilohy || []).length ? z.prilohy.map((p) => (
                  <button key={p.key} type="button" onClick={() => stahnout(p)} className="btn btn-secondary"><Camera className="h-4 w-4" />{p.filename}</button>
                )) : <span className="text-sm text-slate-500">Bez příloh.</span>}
              </div>
            </Sekce>
          </KartaLista>
        </div>

        <div className="min-w-0 space-y-4">
          <KartaLista ikona={MessageSquare} nazev="Podpisy">
            <Podpis kdo={`Zapsal: ${z.zapsal_jmeno || "?"}`} stav={z.podpis_zapsal ? "podepsáno" : "nepodepsáno"} ton={z.podpis_zapsal ? "ok" : "neutralni"}
              detail={z.podpis_zapsal ? `${fmtKdy(z.podpis_zapsal.at)}, ${z.podpis_zapsal.zpusob}` : "záznam je zatím koncept"} />
            <Podpis kdo={`Zraněný: ${z.zraneny_jmeno || "?"}`}
              stav={pz ? (pz.souhlas ? "potvrdil(a)" : "má připomínku") : z.stav === "ceka_podpis" ? "čeká na podpis" : "zatím neodesláno"}
              ton={pz ? (pz.souhlas ? "ok" : "problem") : z.stav === "ceka_podpis" ? "pozor" : "neutralni"}
              detail={pz ? `${fmtKdy(pz.at)}, z telefonu ${pz.telefon || ""}${pz.pripominka ? `. Připomínka: ${pz.pripominka}` : ""}` : z.stav === "ceka_podpis" ? `SMS s odkazem odešla na ${z.zraneny_telefon}` : null}>
              {z.stav === "ceka_podpis" && <button type="button" className="btn btn-secondary" disabled={busy} onClick={() => akce(() => post({ akce: "poslat_znovu", id: z.id }), "SMS odeslána znovu.")}><MessageSquare className="h-4 w-4" />Poslat znovu</button>}
            </Podpis>
            <Podpis kdo="Odpovědná osoba (owner nebo manažer)" stav={z.projednano ? "projednáno" : z.stav === "koncept" ? "čeká na podpisy" : "čeká na projednání"} ton={z.projednano ? "ok" : "znacka"}
              detail={z.projednano ? `${z.projednano.kdo}, ${fmtKdy(z.projednano.at)}${z.projednano.poznamka ? `. ${z.projednano.poznamka}` : ""}${z.projednano.bez_potvrzeni_zraneneho ? ". Uzavřeno bez potvrzení zraněného." : ""}` : null}>
              {prava.projednat && ["ceka_projednani", "ceka_podpis"].includes(z.stav) && <button type="button" className="btn btn-primary" disabled={busy} onClick={() => setProjednat(true)}>Projednat a uzavřít</button>}
            </Podpis>
          </KartaLista>

          <KartaLista ikona={ShieldPlus} nazev="Pojišťovna">
            {poj ? (
              <div className="space-y-3">
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-700"><input type="checkbox" className="h-5 w-5" checked={poj.pojistna_podana} onChange={(e) => setPoj({ ...poj, pojistna_podana: e.target.checked })} />Pojistná událost podána</label>
                <label className="block"><span className={POPISEK}>Název pojišťovny</span><input className="field w-full" value={poj.pojistovna} onChange={(e) => setPoj({ ...poj, pojistovna: e.target.value })} /></label>
                <div className="flex gap-2"><button type="button" className="btn btn-secondary flex-1" onClick={() => setPoj(null)}>Zrušit</button>
                  <button type="button" className="btn btn-primary flex-1" disabled={busy} onClick={() => akce(async () => { await post({ akce: "pojistovna", id: z.id, ...poj }); setPoj(null); }, "Uloženo.")}>Uložit</button></div>
              </div>
            ) : (
              <div className="space-y-2 text-sm text-slate-700">
                <div>Pojistná událost: <b>{z.pojistna_podana ? "podána" : "nepodána"}</b></div>
                <div>Pojišťovna: <b>{z.pojistovna || "nevyplněno"}</b></div>
                {prava.projednat && <button type="button" className="btn btn-secondary" onClick={() => setPoj({ pojistna_podana: !!z.pojistna_podana, pojistovna: z.pojistovna || "" })}><Pencil className="h-4 w-4" />Upravit</button>}
              </div>
            )}
          </KartaLista>

          <KartaLista ikona={Clock} nazev="Historie změn">
            <div className="space-y-1.5 text-[13px] text-slate-600">
              {(data.historie || []).map((h, i) => <div key={i}><b>{fmtKdy(h.created_at)}</b> {h.kdo}: {h.akce}</div>)}
            </div>
          </KartaLista>
        </div>
      </div>

      <Modal open={projednat} onClose={() => setProjednat(false)} size="sm" plnaHlavicka
        header={<HlavickaOkna ikona={FileText} nazev="Projednat a uzavřít" onClose={() => setProjednat(false)} />}
        footer={<button type="button" className="btn btn-primary w-full" disabled={busy} onClick={() => akce(async () => { await post({ akce: "projednat", id: z.id, poznamka }); setProjednat(false); }, "Záznam je uzavřený.")}>Uzavřít záznam</button>}>
        <div className="space-y-3">
          {z.stav === "ceka_podpis" && <div className="rounded-xl bg-amber-50 px-3 py-2.5 text-[13px] font-semibold text-amber-700">Zraněný záznam zatím nepotvrdil. Uzavřením se to zapíše do historie.</div>}
          <label className="block"><span className={POPISEK}>Poznámka k projednání</span><textarea className="field w-full" rows={3} value={poznamka} onChange={(e) => setPoznamka(e.target.value)} placeholder="Např. probráno s trenérem, upravený postup" /></label>
        </div>
      </Modal>
    </Stranka>
  );
}
